import { ReactNode, useRef, useState } from 'react';
import { useMasonryLayout } from '../../hooks/generatePage';
import { PostsGrid, MasonryItem, DragHandle } from './styled';

interface MasonryGridProps<T> {
  items: T[];
  getKey: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  // Si se pasa, las cartas se pueden arrastrar (desde el grip) para reordenar.
  // El nuevo orden se entrega acá; la persistencia (o no) la decide el padre.
  onReorder?: (items: T[]) => void;
  // Señales extra que fuerzan recálculo del layout (ej: thumbnails que llegan).
  layoutDeps?: unknown[];
}

// Grid masonry con reorden por drag-and-drop opcional. El layout (alturas /
// row-span) lo maneja useMasonryLayout; acá solo va la mecánica de arrastre,
// que reordena el array en vivo mientras se arrastra (feedback inmediato).
export function MasonryGrid<T>({
  items, getKey, renderItem, onReorder, layoutDeps = [],
}: MasonryGridProps<T>) {
  const gridRef = useRef<HTMLDivElement>(null);
  const dragFrom = useRef<number | null>(null); // índice de origen del drag activo
  const fromHandle = useRef(false);             // ¿el drag arrancó desde el grip?
  const [dragKey, setDragKey] = useState<string | null>(null);

  const reorderable = !!onReorder;

  // El layout depende de la longitud (alta/baja de cartas) + señales del padre.
  useMasonryLayout(gridRef, [items.length, ...layoutDeps]);

  const move = (from: number, to: number) => {
    if (from === to || !onReorder) return;
    const next = items.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next);
  };

  return (
    <PostsGrid ref={gridRef}>
      {items.map((item, index) => {
        const key = getKey(item);
        return (
          <MasonryItem
            key={key}
            $dragging={dragKey === key}
            draggable={reorderable}
            onDragStart={(e) => {
              // Solo arrancamos el drag si el gesto salió del grip; cualquier
              // otro arrastre (sobre la imagen, etc.) se cancela.
              if (!reorderable || !fromHandle.current) { e.preventDefault(); return; }
              dragFrom.current = index;
              setDragKey(key);
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setDragImage(e.currentTarget, 24, 16);
            }}
            onDragEnter={(e) => {
              if (dragFrom.current === null || dragFrom.current === index) return;
              e.preventDefault();
              move(dragFrom.current, index);
              dragFrom.current = index;
            }}
            onDragOver={(e) => { if (dragFrom.current !== null) e.preventDefault(); }}
            onDragEnd={() => {
              dragFrom.current = null;
              fromHandle.current = false;
              setDragKey(null);
            }}
          >
            {reorderable && (
              <DragHandle
                title="Arrastrar para reordenar"
                onMouseDown={() => { fromHandle.current = true; }}
                onMouseUp={() => { fromHandle.current = false; }}
                onClick={(e) => e.stopPropagation()}
              >
                ⠿
              </DragHandle>
            )}
            {renderItem(item)}
          </MasonryItem>
        );
      })}
    </PostsGrid>
  );
}
